import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { ApiService } from './api.service';
import { ErrorHandlingService } from './error-handling.service';
import { NotificationService } from './notification.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class Authentication {
  refreshToken: string;
  accessToken = new BehaviorSubject<string>(null);

  constructor(
    private _api: ApiService,
    private jwtHelper: JwtHelperService,
    private ngxLoader: NgxUiLoaderService,
    private router: Router,
    private errorHandlingService: ErrorHandlingService,
    private notificationService: NotificationService,
    private userService: UserService,
  ) {}

  login(username: string, password: string): Observable<any> {
    return this._api.postWithoutAuth<any>(`/auth/token`, { username, password, grant_type: 'password' }).pipe(map((data) => data));
  }

  successAuthentication(response: any): void {
    const refreshToken = response.refresh_token;
    const accessToken = response.access_token;
    this.refreshToken = refreshToken;
    this.accessToken.next(accessToken);
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    // Process re-populate the expiration banner
    const userObj = this.jwtHelper.decodeToken(accessToken);
    localStorage.setItem('user_data', JSON.stringify({ id: userObj['termhub:id'] }));

    this.userService.flush();
    this.userService.getUserDetails();
    this.getActiveUserDetails();
    this.ngxLoader.stop();
    this.notificationService.showSuccess('Logged in Successfully');
  }

  errorAuthentication(error: any) {
    this.ngxLoader.stop();
    this.errorHandlingService.handleError(error, error.url);
    if (error.status === 417) {
      this.errorHandlingService.showError(error.message);
      return error;
    }
    if (error.status === 401) {
      this.errorHandlingService.showError(error.message);
      return error;
    }
  }

  whitelistedUrls(): boolean {
    return (
      this.router.url.includes('/login') ||
      this.router.url.includes('/signup') ||
      this.router.url.includes('/email-verify') ||
      this.router.url.includes('/password-reset')
    );
  }

  navigateToReturnUrl(): void {
    if (localStorage.getItem('returnUrl')) {
      this.router.navigateByUrl(localStorage.getItem('returnUrl'));
      localStorage.removeItem('returnUrl');
    } else {
      this.router.navigateByUrl(localStorage.getItem('/dashboard'));
    }
  }

  refreshTokenAndRetry(): Observable<any> {
    if (!localStorage.getItem('refresh_token')) {
      if (!this.router.url.includes('/login') && !this.router.url.includes('/signup') && !this.router.url.includes('/password-reset')) {
        this.errorHandlingService.notAuthenticated();
      }
    }
    const body = {
      grant_type: 'refresh_token',
      refresh_token: localStorage.getItem('refresh_token'),
    };
    return this._api.postWithoutAuth<any>(`/auth/token`, body).pipe(
      tap((response) => {
        const newAccessToken = response.access_token;
        const newRefreshToken = response.refresh_token;
        localStorage.setItem('access_token', newAccessToken);
        localStorage.setItem('refresh_token', newRefreshToken);
        this.accessToken.next(newAccessToken);
      }),
      catchError((error) => {
        return this.errorHandlingService.handleError(error, error.url);
      }),
    );
  }

  getAccessToken(): string {
    return localStorage.getItem('access_token');
  }

  isTokenValid(manualToken?: string): boolean {
    const token = !manualToken ? this.getAccessToken() : manualToken;
    if (token) {
      // valid if not expired
      return !this.jwtHelper.isTokenExpired(token);
    }
    return false;
  }

  isAccountToken(manualToken?: string): boolean {
    const token = !manualToken ? this.getAccessToken() : manualToken;

    if (token) {
      // Decode the token to get claims
      const claims = this.jwtHelper.decodeToken(token);
      // valid if not an ACCOUNT token
      return claims['termhub:role'] !== 'ACCOUNT';
    }
    return false;
  }

  logout(): void {
    this.accessToken.next(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('refresh_token');
    this.refreshToken = null;
    this.router.navigateByUrl('/login');
  }

  getActiveUserDetails(id?: string): any {
    let userId = this.jwtHelper.decodeToken(localStorage.getItem('access_token'));
    userId = userId['termhub:id'] ?? id;
    this._api
      .get(`/user/${userId}`)
      .pipe(map((data) => data))
      .subscribe(
        (data: any) => {
          const userData = JSON.stringify(data);
          localStorage.setItem('user_data', userData);
          this.userService.getUserDetails();
          this.navigateToReturnUrl();
          this.ngxLoader.stop();
        },
        (err) => {
          this.ngxLoader.stop();
          this.errorHandlingService.handleError(err, err.url);
        },
      );
  }

  loginWithGoogle() {
    const results = this._api.getWithResponseHeader(`/auth/google/login`).pipe(map((data) => data));
    return results;
  }

  loginWithMicrosoft() {
    const results = this._api.getWithResponseHeader(`/auth/microsoft/login`).pipe(map((data) => data));
    return results;
  }

  microsoftLoginCallback(query: string) {
    const results = this._api.getWithResponseHeader(`/auth/microsoft/callback?${query}`).pipe(map((data) => data));
    return results;
  }
}
